function normalize_subsections(section, normalize_section) {
  if (!Array.isArray(section?.subsections)) return [];
  return section.subsections.map((child) => normalize_section(child));
}
function get_normalized_section_flags(section) {
  const flags = {
    use_columns: section?.use_columns === true,
    show_on_advert: section?.show_on_advert !== false,
  };
  return flags;
}
function get_description_entry_text(entry) {
  if (typeof entry.value === 'string') return [entry.value];
  if (typeof entry.description === 'string') return [entry.description];
  if (typeof entry.text === 'string') return [entry.text];
  return null;
}
function stringify_description_entry(entry) {
  try {
    return [JSON.stringify(entry)];
  } catch {
    return [String(entry)];
  }
}
function should_parse_description_json(trimmed) {
  return trimmed.startsWith('{') && trimmed.includes('descriptions');
}
function clean_plain_html_text(text) {
  const plain_text = `${text}`.split(/<[^>]+>/g).join(' ').trim();
  return plain_text;
}
function build_normalized_section_base(section) {
  const section_base = {
    id: section?.id || null,
    form_id: section?.form_id || null,
    title: section?.title || '',
    ...get_normalized_section_flags(section),
  };
  return section_base;
}
function expand_null_or_array_description(entry, expand_entry) {
  if (entry == null) return [];
  return Array.isArray(entry) ? entry.flatMap(child => expand_entry(child)) : null;
}

const methods = {
    normalize_sections(raw_sections) {
      if (!Array.isArray(raw_sections)) return [];

      return raw_sections.map(section => this.normalize_section(section));
    },

    normalize_section(section) {
      const normalized_section = {
        ...build_normalized_section_base(section),
        descriptions: this.normalize_descriptions(section?.descriptions || []),
        table: this.normalize_table_for_edit(section?.table),
        subsections: normalize_subsections(section, (child) => this.normalize_section(child)),
      };
      return normalized_section;
    },

    clone_sections_for_edit(sections = []) {
      return this.normalize_sections(JSON.parse(JSON.stringify(sections || []))).map(section => ({
        ...section,
        descriptions: Array.isArray(section.descriptions) && section.descriptions.length > 0
          ? section.descriptions
          : [''],
        subsections: this.clone_subsections_for_edit(section.subsections || []),
      }));
    },

    clone_subsections_for_edit(subsections = []) {
      return (subsections || []).map(subsection => ({
        ...this.normalize_section(subsection),
        descriptions: Array.isArray(subsection?.descriptions) && subsection.descriptions.length > 0
          ? this.normalize_descriptions(subsection.descriptions)
          : [''],
        subsections: [],
      }));
    },

    create_empty_section() {
      const empty_section = {
        id: null,
        form_id: this.form_detail.id,
        title: '',
        descriptions: [''],
        use_columns: false,
        table: { headers: [{ label: 'Column 1' }], rows: [['']] },
        show_on_advert: true,
        subsections: [],
      };
      return empty_section;
    },

    create_empty_subsection() {
      const empty_subsection = {
        id: null,
        form_id: this.form_detail.id,
        title: '',
        descriptions: [''],
        use_columns: false,
        table: { headers: [{ label: 'Column 1' }], rows: [['']] },
        show_on_advert: true,
        subsections: [],
      };
      return empty_subsection;
    },

    normalize_table_for_edit(table) {
      if (!table) return this.get_empty_edit_table();

      const normalizedHeaders = Array.isArray(table.headers)
        ? table.headers.map(hdr => typeof hdr === 'string' ? { label: hdr } : hdr)
        : [{ label: 'Column 1' }];

      const normalized_table = {
        ...table,
        headers: normalizedHeaders,
        rows: table.rows || [['']]
      };
      return normalized_table;
    },
    get_empty_edit_table() {
      const empty_table = { headers: [{ label: 'Column 1' }], rows: [['']] };
      return empty_table;
    },

    normalize_descriptions(raw) {
      if (!raw) return [];
      if (!Array.isArray(raw)) return [String(raw)];

      return raw
        .flatMap(entry => this.expand_description_entry(entry))
        .map(text => typeof text === 'string' ? text.trim() : String(text))
        .filter(line => line && line.length > 0);
    },

    expand_description_entry(entry) {
      const expanded = expand_null_or_array_description(entry, (child) => this.expand_description_entry(child));
      if (expanded !== null) return expanded;
      if (typeof entry === 'object') return this.expand_description_object(entry);
      if (typeof entry === 'string') return this.expand_description_string(entry);
      return [String(entry)];
    },
    expand_description_object(entry) {
      if (Array.isArray(entry.descriptions)) return entry.descriptions.flatMap(child => this.expand_description_entry(child));
      return get_description_entry_text(entry) || stringify_description_entry(entry);
    },
    expand_description_string(entry) {
      const trimmed = entry.trim();
      if (should_parse_description_json(trimmed)) return this.expand_description_json(trimmed, entry);
      return [entry];
    },
    expand_description_json(trimmed, fallback) {
      const parsed = this.parse_description_json(trimmed);
      return parsed ? this.expand_description_entry(parsed) : [fallback];
    },
    parse_description_json(trimmed) {
      let parsed = null;
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        parsed = null;
      }
      return parsed;
    },

    strip_html_tags(text) {
      if (!text) return '';
      return clean_plain_html_text(text);
    },

    clean_html(html) {
      if (!html) return '';
      const clean_text = `${html}`.split(/<p>\s*<\/p>/g).join('').trim();
      return this.convert_plain_text_newlines(clean_text);
    },
    remove_empty_legacy_paragraphs(html) {
      const clean_html = `${html}`.split(/<p>\s*<\/p>/g).join('').trim();
      return clean_html;
    },
    convert_plain_text_newlines(str) {
      if (str.includes('<') || !str.includes('\n')) return str;
      return str.split('\n').join('<br>');
    },

    has_table_structure(section) {
      return section.use_columns === true;
    },

    has_table_content(section) {
      if (!section.table || !Array.isArray(section.table.rows)) return false;
      return section.table.rows.some((row) => this.has_table_row_content(row));
    },
    has_table_row_content(row) {
      return Array.isArray(row) && row.some((cell) => this.strip_html_tags(cell || '').length > 0);
    },
};

export default methods;
